'use client';

import { clsx } from 'clsx';
import useSWRInfinite from 'swr/infinite';
import { useSearchParams } from 'next/navigation';
import { useContext, useMemo } from 'react';

import { RealEstateDict } from '@monoimin/intl';

import { FormButton } from '@monoimin/atoms';

import {
  createFilterParamsFromConfigs,
  extendFilterConfigs,
} from '../utils/filters';
import { pickDefaultParamFromSortConfigs } from '../utils/sorting';
import {
  ESTATES_PAGE_SIZE,
  EMPTY_ESTATES_ARRAY,
  ESTATE_SORTING_PARAMS,
  REAL_ESTATE_LIST_CLASSES,
} from '../constants';
import { getStrapiEstates } from '../utils/getStrapiEstates';
import { defineObjectsTotalCount } from '../utils';

import { FiltersContext } from './filters/FiltersProvider';
import { RealEstateCard } from './RealEstateCard';

import { Sorting } from './Sorting';

import { RealEstateListLoader } from './RealEstateListLoader';

import type {
  EnOrRuLocale,
  Locales,
  StrapiFilterParams,
} from '@monoimin/sb-types';
import type {
  FilterConfigs,
  SortConfigs,
  RealEstateCardVariant,
} from '../types';

interface RealEstateListProps {
  locale: EnOrRuLocale;
  country?: number;
  realEstateDict: RealEstateDict;
  cardVariant: RealEstateCardVariant;
  containerClassName?: string;
  permanentFilters?: StrapiFilterParams;
  sortConfigs: SortConfigs;
  filterConfigs: FilterConfigs;
  isPassportivity?: boolean;
}

const fetcher = async (args: {
  page: number;
  locale: Locales;
  sortParam: string | null;
  filterParams: StrapiFilterParams | undefined;
}) => {
  const { page, locale, sortParam, filterParams } = args;

  const sort =
    ESTATE_SORTING_PARAMS[sortParam as keyof typeof ESTATE_SORTING_PARAMS];

  return getStrapiEstates({
    pagination: {
      page: page,
      pageSize: ESTATES_PAGE_SIZE,
    },
    sort: sort,
    locale: locale,
    filters: filterParams,
  });
};

export const RealEstateList = ({
  locale,
  realEstateDict,
  country,
  cardVariant,
  containerClassName,
  permanentFilters = {},
  sortConfigs,
  filterConfigs,
  isPassportivity,
}: RealEstateListProps) => {
  const [filtersState] = useContext(FiltersContext);

  const searchParams = useSearchParams();

  const defaultSortParam = pickDefaultParamFromSortConfigs(sortConfigs);

  const sortParam = searchParams?.get(`sort`) || defaultSortParam;

  const filterParams = useMemo(() => {
    if (!filtersState) {
      return {};
    }

    const extendedFilterConfigs = extendFilterConfigs(filterConfigs);

    const configsFilterPararams = createFilterParamsFromConfigs(
      extendedFilterConfigs,
      {
        ...filtersState,
        country:
          country && Array.isArray(filtersState.country)
            ? [...filtersState.country, country]
            : filtersState.country,
      }
    );

    if (configsFilterPararams) {
      return Object.assign(configsFilterPararams, permanentFilters);
    }

    return permanentFilters;
  }, [filterConfigs, filtersState, country, permanentFilters]);

  const { data, size, setSize, isLoading } = useSWRInfinite(
    (page) => {
      return {
        sortParam: sortParam,
        page: page + 1,
        locale: locale,
        filterParams: filterParams,
      };
    },
    fetcher,
    {
      revalidateFirstPage: false,
    }
  );

  const haveData = data && Array.isArray(data) && data.every(Boolean);

  const estatesData = haveData ? data.map((item) => item.estates) : undefined;

  const estates = estatesData
    ? EMPTY_ESTATES_ARRAY.concat(...estatesData)
    : EMPTY_ESTATES_ARRAY;

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === `undefined`);

  const isEmpty = data?.[0]?.estates?.length === 0;

  const isReachingEnd =
    isEmpty ||
    (data && data[data.length - 1]?.estates?.length < ESTATES_PAGE_SIZE);

  const totalCount = haveData ? data[0].totalCount : 0;

  if (isLoadingMore) {
    return (
      <RealEstateListLoader
        cardVariant={cardVariant}
        containerClassName={containerClassName}
      />
    );
  }

  return (
    <div
      className={clsx(REAL_ESTATE_LIST_CLASSES.container, containerClassName)}
    >
      <div className={REAL_ESTATE_LIST_CLASSES.sortingContainer}>
        <span className='real-estate-list__counter'>
          {defineObjectsTotalCount(totalCount, locale)}
        </span>
        <Sorting
          configs={sortConfigs}
          locale={locale}
          defaultSortParam={defaultSortParam}
        />
      </div>
      {Boolean(estates?.length) && (
        <div
          data-testid='real-estate-list-cards-container'
          className={REAL_ESTATE_LIST_CLASSES.cardsContainer[cardVariant]}
        >
          {estates.map((item) => {
            return (
              <RealEstateCard
                key={item?.id}
                variant={cardVariant}
                data={item}
                locale={locale}
                isPassportivity={isPassportivity}
              />
            );
          })}
        </div>
      )}

      {isReachingEnd && isEmpty && (
        <div className='flex h-44 items-center'>
          <p className='h2 text-center'>{realEstateDict.noSearchResults}</p>
        </div>
      )}
      {!isReachingEnd && (
        <FormButton
          variant='secondary'
          className='w-full sm:w-fit mx-auto'
          onClick={() => setSize(size + 1)}
        >
          {isLoadingMore ? realEstateDict.loading : realEstateDict.showMore}
        </FormButton>
      )}
    </div>
  );
};
